"use client"

import type { GlossaryFrontmatter } from "@/lib/content/schemas/glossary";

import React, { useEffect, useRef, useState } from "react"
import Link from "next/link"
import clsx from "clsx"

export default function GlossaryTooltip({
  term,
  definition,
}: {
  term: Pick<GlossaryFrontmatter, "title" | "primaryButton">
  // The term definition, pre-rendered by the server and passed down as a ReactNode.
  definition?: React.ReactNode
}) {
  const tip = useRef<SVGSVGElement>(null)
  const tooltipWrapper = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState("top")

  const handleMouseMove = (e: MouseEvent) => {
    if (!tip.current || !tooltipWrapper.current) return

    const tipRect = tip.current.getBoundingClientRect()
    const x =
      e.clientX -
      tooltipWrapper.current.getBoundingClientRect().left -
      tipRect.width / 2

    tip.current.style.left = `${x - 4}px`
  }

  const handleMouseLeave = () => {
    document.addEventListener("mousemove", handleMouseMove)
  }

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [])

  const handleScroll = () => {
    if (!tooltipWrapper.current) return

    const tooltipRect = tooltipWrapper.current.getBoundingClientRect()
    const windowHeight = window.innerHeight

    if (tooltipRect.top < 100) {
      setPosition("bottom")
    } else if (
      tooltipRect.bottom > windowHeight ||
      (tooltipRect.top >= 100 && position === "bottom")
    ) {
      setPosition("top")
    }
  }

  useEffect(() => {
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!term) return null

  return (
    <div
      ref={tooltipWrapper}
      className={clsx(
        "absolute left-1/2 z-40 -translate-x-1/2 w-full",
        position === "top" ? "bottom-0 pb-6" : "top-0 pt-8",
        "pointer-events-none opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto transition-all cursor-help"
      )}
    >
      <div
        onMouseLeave={handleMouseLeave}
        className="text-sm relative mb-2 min-w-64 w-auto px-3 py-2 bg-grey-850 rounded-lg z-10 cursor-default text-grey-400 group-hover:top-0 top-2 transition-all duration-300 ease-in-out group-hover:shadow-xl group-hover:shadow-grey-950/50"
      >
        <strong className="text-white mb-1 font-semibold">{term.title}</strong>
        <div id="glossary-tooltip" className="mb-4 w-full">
          {definition}
        </div>
        <div className="flex gap-1.5 mb-[5px] items-center">
          {term.primaryButton && term.primaryButton.url && (
            <Link
              className="bg-red-500 truncate leading-none px-2 py-1.5 rounded m-0 text-white font-medium h-[26px] transition-colors duration-150 hover:bg-red-500/75"
              href={term.primaryButton.url}
              passHref
            >
              {term.primaryButton.title || "Learn more"}
            </Link>
          )}
          <Link
            href={"/glossary?term=" + term.title.toLowerCase() + "#" +
              term.title.charAt(0).toUpperCase()}
            passHref
            className="bg-grey-800 leading-none px-2 py-1.5 rounded m-0 text-white font-medium h-[26px] transition-colors hover:bg-grey-800/75 duration-150"
            prefetch={false}
          >
            Glossary
          </Link>
        </div>
        <div
          className={clsx(
            "absolute w-[calc(100%-1rem)] overflow-hidden mx-auto left-0 right-0 h-2",
            (position === "top"
              ? "bottom-0 -mb-2 rounded-b-full"
              : "top-0 -mt-2 rounded-t-full")
          )}
        >
          <svg
            ref={tip}
            width="16"
            height="8"
            viewBox="0 0 16 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={clsx(
              "absolute left-3 text-grey-850",
              position === "top" ? "bottom-0" : "top-0 rotate-180"
            )}
          >
            <path
              d="M6.54597 6.93103L0 0H16L9.45403 6.93103C8.66478 7.7667 7.33522 7.7667 6.54597 6.93103Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}
